import { ReportSharp } from "@mui/icons-material";
import { useAuthHeader } from "react-auth-kit";
import { useQuery } from "react-query";
import axiosInstance from "../../utils/AxiosInstance";
import { toast } from "react-toastify";
import { useGetPatientById, useGetPatients } from "../PatientComponents";
import {
  convertServerToLocalDatetime,
  renderDateUTC,
  renderEUDateTime,
} from "../../utils/utils";
import dayjs from "dayjs";

export const reportTypes = [
  { label: "SPO2", id: 1 },
  { label: "ECG", id: 2 },
  { label: "Pressione", id: 3 },
  { label: "Temperatura", id: 4 },
];

// GET /reports/patient/:patient_id?report_type=Int
export const useGetPatientReports = (patient_id, report_type) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["reports", "patient", patient_id, report_type],
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/reports/patient/${patient_id}?report_type=${report_type}`,
        {
          headers: { Authorization: authHeader() },
        }
      );

      // remove useless reports (just local-wise)
      let data = res.data;
      let reports = [];
      data.reports.forEach((el, index) => {
        if (el.data.hasOwnProperty("Date")) {
          el.data.Date = convertServerToLocalDatetime(el.data.Date);
          reports.push(el);
        }
      });

      data["reports"] = reports;
      return data;
    },
  });
};

// GET /reports/:report_id
export const useGetReportById = (report_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["reports", report_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/reports/${report_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
  });
};

// GET /reports/daily
export const useGetDailyReports = (_date) => {
  const authHeader = useAuthHeader();
  const patientsQuery = useGetPatients();
  // const date = new Date(_date);
  const urlDate = _date.toJSON().substring(0, 10);

  return useQuery({
    queryKey: ["reports", "daily", urlDate],
    queryFn: async () => {
      const res = await axiosInstance.get(`/reports/daily?date=${urlDate}`, {
        headers: { Authorization: authHeader() },
      });
      let data = res.data;

      let arr = processRecapData(data, patientsQuery.data.patients);
      return [...arr];
    },
    enabled: patientsQuery.isSuccess, // && _date !== null && _date !== undefined,
  });
};

export const useGetRangeOfDailyReports = (startDate, endDate) => {
  const authHeader = useAuthHeader();
  const patientsQuery = useGetPatients();

  const range = dayjs(endDate).diff(dayjs(startDate), "day");
  let dailyreports = [];

  return useQuery({
    queryKey: ["reports", "daily", startDate, endDate],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      // if dates have not been set, just return null
      if (startDate == endDate) {
        return null;
      }

      // iterate through date range otherwise
      for (let i = 0; i <= range; i++) {
        const date = dayjs(startDate).add(i, "day").format("YYYY-MM-DD");
        const res = await axiosInstance.get(`/reports/daily?date=${date}`, {
          headers: { Authorization: authHeader() },
        });

        if (res.data.length === 0) {
          continue;
        }

        let arr = processRecapData(res.data, patientsQuery.data.patients);
        let dr = [...arr];
        dailyreports.push(dr);
      }
      console.log(dailyreports);
      return dailyreports;
    },
    enabled: patientsQuery.isSuccess, // && _date !== null && _date !== undefined,
  });
};

// GET /reports/recap
export const useGetRecap = () => {
  const authHeader = useAuthHeader();
  const patientsQuery = useGetPatients();

  return useQuery({
    queryKey: ["reports", "recap"],
    queryFn: async () => {
      const res = await axiosInstance.get(`/reports/recap`, {
        headers: { Authorization: authHeader() },
      });
      if (res === undefined || res === null) return [];

      let data = res.data;

      // for each patient, I have an object containing patient infos and the list of reports
      let recapArray = processRecapData(data, patientsQuery.data.patients);

      return [...recapArray];
    },
    enabled: patientsQuery.isSuccess,
  });
};

const processRecapData = (data, patients) => {
  let recap = [];
  data.forEach((el, index) => {
    const patient = patients.find((patient) => patient.id === el.patient_id);

    let recapElement = {
      patient_id: patient.id,
      org_id: patient.org_id,
      org_name: patient.org_name,
      first_name: patient === undefined ? "" : patient.first_name,
      last_name: patient === undefined ? "" : patient.last_name,
      isOutOfBounds: false,
      alert: el.alert, // ? alert è uguale per ogni report di un certo paziente? cioe, sta ad indicare una proprieta del report o del paziente in generale?
    };

    // check if report has out of bounds values
    let outOfBounds = {
      Temperature: false,
      Pmax: false,
      Pmin: false,
      HR: false,
      SPO2: false,
    };

    let temp1 = false,
      temp2 = false;

    // temperature
    temp1 =
      el.threashold.temp_max === null
        ? false
        : el.data.Temperature > el.threashold.temp_max;
    temp2 =
      el.threashold.temp_min === null
        ? false
        : el.data.Temperature < el.threashold.temp_min;
    outOfBounds["Temperature"] = temp1 || temp2;

    // pmax
    temp1 =
      el.threashold.press_sis_max === null
        ? false
        : el.data.Pmax > el.threashold.press_sis_max;
    temp2 =
      el.threashold.press_sis_min === null
        ? false
        : el.data.Pmax < el.threashold.press_sis_min;
    outOfBounds["Pmax"] = temp1 || temp2;

    // pmin
    temp1 =
      el.threashold.press_dia_max === null
        ? false
        : el.data.Pmin > el.threashold.press_dia_max;
    temp2 =
      el.threashold.press_dia_min === null
        ? false
        : el.data.Pmin < el.threashold.press_dia_min;
    outOfBounds["Pmin"] = temp1 || temp2;

    // hr
    temp1 =
      el.threashold.pulse_max === null
        ? false
        : el.data.HR > el.threashold.pulse_max;
    temp2 =
      el.threashold.pulse_min === null
        ? false
        : el.data.HR < el.threashold.pulse_min;
    outOfBounds["HR"] = temp1 || temp2;

    // hr
    temp1 =
      el.threashold.spo2_max === null
        ? false
        : el.data.SPO2 > el.threashold.spo2_max;
    temp2 =
      el.threashold.spo2_min === null
        ? false
        : el.data.SPO2 < el.threashold.spo2_min;
    outOfBounds["SPO2"] = temp1 || temp2;

    // add current report
    recapElement["recap"] = {
      date: el.date,
      data: { ...el.data },
      thresholds: { ...el.threashold },
      outOfBounds: outOfBounds,
    };

    // add patient_id to the type of users it belongs
    recapElement.isOutOfBounds = Object.values(outOfBounds).some(Boolean);

    recap.push(recapElement);
  });

  // to array then sort it by isOutOfBounds - problematic users first
  const recapArray = recap.sort((a, b) => b.isOutOfBounds - a.isOutOfBounds);

  return [...recapArray];
};
